import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Lock, Eye, Database, Globe, UserCheck, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { privacyContentEN } from '../content/privacy-en';
import { privacyContentET } from '../content/privacy-et';
import { privacyContentAR } from '../content/privacy-ar';
import PrivacyContentRussian from './PrivacyContentRussian';

const PrivacyPolicy = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  
  // Get content based on language
  const getContentByLang = () => {
    switch(currentLang) {
      case 'en': return privacyContentEN;
      case 'et': return privacyContentET;
      case 'ar': return privacyContentAR;
      default: return null; // Russian content is hardcoded below
    }
  };
  
  const content = getContentByLang();
  
  // Translations for page elements
  const translations = {
    ru: {
      title: 'Политика конфиденциальности',
      subtitle: 'Последнее обновление: 6 ноября 2025',
      backButton: 'Вернуться на главную',
      tableOfContents: 'Содержание',
      important: 'Важно',
      contact: 'Контакты:'
    },
    en: {
      title: 'Privacy Policy',
      subtitle: 'Last updated: 6 November 2025',
      backButton: 'Back to main page',
      tableOfContents: 'Table of Contents',
      important: 'Important',
      contact: 'Contact:'
    },
    et: {
      title: 'Privaatsuspoliitika',
      subtitle: 'Uuendatud: 6. november 2025',
      backButton: 'Tagasi avalehele',
      tableOfContents: 'Sisukord',
      important: 'Tähtis',
      contact: 'Kontakt:'
    },
    ar: {
      title: 'سياسة الخصوصية',
      subtitle: 'آخر تحديث: 6 نوفمبر 2025',
      backButton: 'العودة إلى الصفحة الرئيسية',
      tableOfContents: 'جدول المحتويات',
      important: 'مهم',
      contact: 'اتصل بنا:'
    }
  };
  
  const lang = translations[currentLang] || translations['ru'];
  const sections = content?.sections || [
    { id: 'general', title: '1. Общие положения' },
    { id: 'data', title: '2. Какие данные мы обрабатываем' },
    { id: 'purposes', title: '3. Цели и правовые основания' },
    { id: 'cookies', title: '4. Cookies и аналитика' },
    { id: 'ai-services', title: '5. AI и облачные сервисы' },
    { id: 'transfer', title: '6. Передача и хранение данных' },
    { id: 'rights', title: '7. Права субъектов данных' },
    { id: 'security', title: '8. Безопасность' },
    { id: 'contacts', title: '9. Контакты' }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-gray-800 py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <a 
            href={currentLang === 'et' ? '/' : `/${currentLang}`}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            {lang.backButton}
          </a>
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10 text-blue-400" />
            <div>
              <h1 className="text-4xl font-bold text-white mb-2 text-left rtl:text-right">
                {lang.title}
              </h1>
              <p className="text-gray-400 text-left rtl:text-right">
                {lang.subtitle}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Table of Contents */}
          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:w-64 flex-shrink-0"
          >
            <div className="sticky top-24 bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-800">
              <h2 className="text-lg font-semibold text-white mb-4 text-left rtl:text-right">{lang.tableOfContents}</h2>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="block text-gray-400 hover:text-white transition-colors text-sm py-1 text-left rtl:text-right"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>
          </motion.aside>

          {/* Main Content */}
          <motion.main
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex-1 space-y-8"
          >
            {/* GDPR Notice */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6 flex gap-4">
              <Lock className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-green-400 mb-2">GDPR Compliance</h3>
                <p className="text-gray-300 leading-relaxed text-left rtl:text-right">
                  {currentLang === 'ru' && 'Настоящая Политика соответствует требованиям Регламента (ЕС) 2016/679 (GDPR) и Закона Эстонии о защите персональных данных. Ваши данные защищены на самом высоком европейском уровне.'}
                  {content?.gdprNotice || (currentLang !== 'ru' && 'This Policy complies with the requirements of Regulation (EU) 2016/679 (GDPR) and the Estonian Personal Data Protection Act. Your data is protected at the highest European level.')}
                </p>
              </div>
            </div>

            {/* Render content for EN/ET/AR */}
            {content && currentLang !== 'ru' && (
              <PrivacyContentTranslated content={content} currentLang={currentLang} />
            )}

            {/* Russian content - keep existing */}
            {currentLang === 'ru' && (
              <PrivacyContentRussian />
            )}
          </motion.main>
        </div>
      </div>
    </div>
  );
};

// Component for translated content (EN/ET/AR)
const PrivacyContentTranslated = ({ content, currentLang }) => {
  return (
    <>
      {/* Section 1 - General */}
      <section id="general" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800 text-left rtl:text-right">
          {content.general.title}
        </h2>
        <div className="space-y-4 text-gray-300 leading-relaxed text-left rtl:text-right" style={{whiteSpace: 'pre-line'}}>
          {content.general.content}
        </div>
      </section>

      {/* Section 2 - Data */}
      <section id="data" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800 text-left rtl:text-right">
          {content.data.title}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {content.data.categories.map((category, idx) => {
            const IconComponent = category.icon === 'Eye' ? Eye : category.icon === 'Database' ? Database : category.icon === 'Globe' ? Globe : AlertCircle;
            return (
              <div key={idx} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <IconComponent className="w-6 h-6 text-blue-400" />
                  <h3 className="text-xl font-semibold text-white text-left rtl:text-right">{category.name}</h3>
                </div>
                <ul className="space-y-2 text-gray-400 text-left rtl:text-right">
                  {category.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Section 3 - Purposes */}
      <section id="purposes" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800 text-left rtl:text-right">
          {content.purposes.title}
        </h2>
        <p className="text-gray-300 mb-6 text-left rtl:text-right">{content.purposes.intro}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {content.purposes.bases.map((base, idx) => (
            <div key={idx} className={`bg-${base.color}-500/10 border border-${base.color}-500/30 rounded-xl p-6`}>
              <h3 className="text-lg font-semibold text-white mb-2 text-left rtl:text-right">{base.title}</h3>
              <p className="text-sm text-gray-400 mb-2 text-left rtl:text-right">{base.legal}</p>
              <p className="text-gray-300 text-left rtl:text-right">{base.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Section 4 - Cookies */}
      <section id="cookies" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800 text-left rtl:text-right">
          {content.cookies.title}
        </h2>
        <p className="text-gray-300 mb-4 text-left rtl:text-right">{content.cookies.intro}</p>
        <div className="space-y-3">
          {content.cookies.services.map((service, idx) => (
            <div key={idx} className="flex items-start gap-3 bg-gray-800/30 rounded-lg p-4">
              <span className="text-blue-400">•</span>
              <div>
                <strong className="text-white">{service.name}:</strong>
                <span className="text-gray-400"> {service.description}</span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-gray-400 mt-4 text-left rtl:text-right">{content.cookies.note}</p>
        <a href="/cookie-policy" className="text-blue-400 hover:underline">{content.cookies.linkText}</a>
      </section>

      {/* Section 5 - AI Services */}
      <section id="ai-services" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800 text-left rtl:text-right">
          {content.aiServices.title}
        </h2>
        <p className="text-gray-300 mb-4 text-left rtl:text-right">{content.aiServices.intro}</p>
        <ul className="list-disc list-inside text-gray-400 space-y-2 text-left rtl:text-right">
          {content.aiServices.platforms.map((platform, idx) => (
            <li key={idx}>{platform}</li>
          ))}
        </ul>
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mt-4">
          <p className="text-gray-300 text-left rtl:text-right">{content.aiServices.transferNote}</p>
        </div>
      </section>

      {/* Section 6 - Transfer */}
      <section id="transfer" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800 text-left rtl:text-right">
          {content.transfer.title}
        </h2>
        <p className="text-gray-300 mb-4 text-left rtl:text-right">{content.transfer.intro}</p>
        <div className="space-y-4">
          {content.transfer.recipients.map((recipient, idx) => (
            <div key={idx} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-2 text-left rtl:text-right">{recipient.title}</h4>
              <p className="text-gray-400 text-left rtl:text-right">{recipient.description}</p>
            </div>
          ))}
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mt-6">
          <h4 className="text-white font-semibold mb-2 text-left rtl:text-right">{content.transfer.retention.title}</h4>
          <p className="text-gray-300 text-left rtl:text-right">
            <strong className="text-blue-400">{content.transfer.retention.period}</strong> {content.transfer.retention.note}
          </p>
        </div>
      </section>

      {/* Section 7 - Rights */}
      <section id="rights" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800 text-left rtl:text-right">
          {content.rights.title}
        </h2>
        <p className="text-gray-300 mb-4 text-left rtl:text-right">{content.rights.intro}</p>
        <div className="space-y-3">
          {content.rights.list.map((right, idx) => (
            <div key={idx} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
              <h4 className="text-white font-semibold mb-1 text-left rtl:text-right">{right.title}</h4>
              <p className="text-gray-400 text-left rtl:text-right">{right.description}</p>
            </div>
          ))}
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mt-6">
          <h4 className="text-lg font-semibold text-green-400 mb-3 text-left rtl:text-right">{content.rights.exercise.title}</h4>
          <p className="text-gray-300 mb-3 text-left rtl:text-right">{content.rights.exercise.instruction}</p>
          <p className="text-gray-400 text-sm text-left rtl:text-right">{content.rights.exercise.complaint}</p>
        </div>
      </section>

      {/* Section 8 - Security */}
      <section id="security" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800 text-left rtl:text-right">
          {content.security.title}
        </h2>
        <p className="text-gray-300 mb-6 text-left rtl:text-right">{content.security.intro}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {content.security.measures.map((measure, idx) => {
            const IconComponent = measure.icon === 'Lock' ? Lock : measure.icon === 'Shield' ? Shield : Database;
            return (
              <div key={idx} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center">
                <IconComponent className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                <h4 className="text-white font-semibold mb-2">{measure.title}</h4>
                <p className="text-gray-400 text-sm">{measure.description}</p>
              </div>
            );
          })}
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <h4 className="text-white font-semibold mb-2 text-left rtl:text-right">{content.security.breach.title}</h4>
          <p className="text-gray-300 text-left rtl:text-right">{content.security.breach.text}</p>
        </div>
      </section>

      {/* Section 9 - Contacts */}
      <section id="contacts" className="scroll-mt-32">
        <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-gray-800 text-left rtl:text-right">
          {content.contacts.title}
        </h2>
        <p className="text-gray-300 mb-4 text-left rtl:text-right">{content.contacts.intro}</p>
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <p className="text-white font-semibold mb-2 text-left rtl:text-right">{content.contacts.company}</p>
          <p className="text-gray-400 text-left rtl:text-right">{currentLang === 'en' && 'Reg. No.'}{currentLang === 'et' && 'Reg. nr.'}{currentLang === 'ar' && 'رقم التسجيل'} {content.contacts.regNumber}</p>
          <p className="text-gray-400 text-left rtl:text-right">{content.contacts.address}</p>
          <p className="text-gray-400 mt-3 text-left rtl:text-right">
            <a href={`mailto:${content.contacts.email}`} className="text-blue-400 hover:underline">
              {content.contacts.email}
            </a>
          </p>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
